import React from 'react';
import { Card, Row, Col, Statistic, Divider, Tooltip } from 'antd';
import { InfoCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../utils/danishNumberFormatter';
import { COLORS } from '../../constants/theme';

/**
 * Combined Company Financial Dashboard
 * Shows complete financial overview as if all operations were one company
 */
const CombinedCompanyFinancials = ({ financialData, stats, config }) => {
  if (!financialData || !financialData.combined) {
    return null;
  }

  const { combined, heat, electricity } = financialData;

  // Calculate complete financial metrics
  const calculateMetrics = () => {
    // Revenue breakdown (from both companies)
    const revenues = {
      varmesalg: heat.revenue.varmesalg || 0,
      elsalgTurbine: heat.revenue.elsalgTurbine || 0,
      elsalgBatteri: electricity.revenue.elsalgBatteri || 0,
      systemydelserElkedel: electricity.revenue.systemydelserElkedel || 0,
      systemydelserBatteri: electricity.revenue.systemydelserBatteri || 0,
      systemydelserTurbine: heat.revenue.systemydelserTurbine || 0,
      kapacitetsbetaling: (heat.revenue.kapacitetsbetaling || 0) + (electricity.revenue.kapacitetsbetaling || 0),
    };

    // OPEX breakdown
    const opex = {
      variable: {
        elkøb: (heat.opex.elkøbOperational || 0) + (electricity.opex.elkøbArbitrage || 0),
        sol: heat.opex.solOperational || 0,
        overskudsvarme: heat.opex.overskudsvarme || 0,
      },
      fixed: {
        forsyningDkLicens: heat.opex.forsyningDkLicens || 0,
        it: heat.opex.it || 0,
        revision: heat.opex.revision || 0,
        administration: heat.opex.administration || 0,
        driftVedligehold: heat.opex.driftVedligehold || 0,
      },
    };

    // CAPEX breakdown (all from allocation)
    const capex = {
      ...heat.capex,
      ...electricity.capex,
    };

    // Depreciation breakdown
    const depreciation = {
      ...heat.depreciation,
      ...electricity.depreciation,
    };

    // Calculate key metrics
    const totalVariableOpex = Object.values(opex.variable).reduce((sum, val) => sum + val, 0);
    const totalFixedOpex = Object.values(opex.fixed).reduce((sum, val) => sum + val, 0);
    
    // Financial KPIs
    const totalRevenue = combined.totalRevenue;
    const totalOpex = combined.totalOpex;
    const ebitda = combined.ebitda;
    const totalDepreciation = combined.totalDepreciation * 1000000;
    const netProfit = combined.netProfit;
    const totalCapex = combined.totalCapex * 1000000;
    
    const paybackYears = ebitda > 0 ? totalCapex / ebitda : 0;
    const ebitdaMargin = totalRevenue > 0 ? (ebitda / totalRevenue * 100) : 0;
    const netMargin = totalRevenue > 0 ? (netProfit / totalRevenue * 100) : 0;
    
    // NPV calculation (25 years, 5% discount rate)
    const discountRate = 0.05;
    const projectLife = 25;
    let npv = -totalCapex;
    for (let year = 1; year <= projectLife; year++) {
      npv += ebitda / Math.pow(1 + discountRate, year);
    }
    
    // IRR approximation
    const irr = paybackYears > 0 && paybackYears < projectLife ? 
      (Math.pow(1/paybackYears, 1/projectLife) - 1) * 100 : 0;
    
    // LCOE calculation
    let npvEnergy = 0;
    let npvCosts = totalCapex;
    const annualHeatDelivery = stats.netDelivery || 0;
    for (let year = 1; year <= projectLife; year++) {
      npvEnergy += annualHeatDelivery / Math.pow(1 + discountRate, year);
      npvCosts += totalOpex / Math.pow(1 + discountRate, year);
    }
    const lcoe = npvEnergy > 0 ? npvCosts / npvEnergy : 0;
    
    // Heat price calculations
    // Get actual energy volumes from stats (kun operational, IKKE arbitrage)
    const solarEnergy = (stats.operationalSolarPurchased || 0); // MWh
    const gridEnergy = (stats.operationalGridPurchased || 0); // MWh
    const wasteHeatEnergy = (stats.operationalFactoryWasteHeatUsed || 0); // MWh

    // 1. Værkets produktionspris (kun Sol + Elnet operational) - går gennem an-værk/ab-værk
    const plantEnergy = solarEnergy + gridEnergy; // MWh
    const plantCost = (heat.opex.elkøbOperational || 0) + (heat.opex.solOperational || 0); // DKK - KUN operational!
    const plantProductionPrice = plantEnergy > 0 ? plantCost / plantEnergy : 0;

    // 2. Total rå net pris (inkl. fabriksoverskudsvarme) - går direkte til LTES
    const totalRawEnergy = solarEnergy + gridEnergy + wasteHeatEnergy; // MWh
    const totalRawCost = plantCost + (heat.opex.overskudsvarme || 0); // DKK
    const totalRawNetPrice = totalRawEnergy > 0 ? totalRawCost / totalRawEnergy : 0;

    // 3. OPEX varmepris (KUN varmeselskabets OPEX, uden afskrivning)
    const heatCompanyOpex = heat.totalOpex;
    const opexHeatPrice = annualHeatDelivery > 0 ? heatCompanyOpex / annualHeatDelivery : 0;

    // 4. Fuld varmepris til kunder (KUN varmeselskabets OPEX + afskrivninger)
    const heatCompanyDepreciation = heat.totalDepreciation * 1000000; // Convert from M DKK to DKK
    const fullHeatPrice = annualHeatDelivery > 0 ? (heatCompanyOpex + heatCompanyDepreciation) / annualHeatDelivery : 0;

    // Calculate hours and energy metrics
    const totalHours = 8783;
    const heatingHours = (stats.fullPurchaseHours || 0) + (stats.partialPurchaseHours || 0);
    const serviceHours = totalHours - heatingHours;
    
    return {
      revenues,
      opex,
      capex,
      depreciation,
      totalRevenue,
      totalOpex,
      totalVariableOpex,
      totalFixedOpex,
      ebitda,
      totalDepreciation,
      netProfit,
      totalCapex,
      paybackYears,
      ebitdaMargin,
      netMargin,
      npv,
      irr,
      lcoe,
      plantProductionPrice,
      plantEnergy,
      totalRawNetPrice,
      totalRawEnergy,
      opexHeatPrice,
      fullHeatPrice,
      heatingHours,
      serviceHours,
      totalHours,
      discountRate: discountRate * 100,
      projectLife,
    };
  };

  const metrics = calculateMetrics();

  return (
    <div>
      <h3 style={{ 
        color: COLORS.primary.green, 
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <AppstoreOutlined /> Samlet Finansielt Overblik
      </h3>

      {/* Row 1: P&L, Nøgletal, Indtægter */}
      <Row gutter={[16, 16]}>
        {/* Resultatopgørelse */}
        <Col span={8}>
          <Card 
            title={
              <span>
                📊 Resultatopgørelse
                <Tooltip title="Årlig resultatopgørelse viser omsætning, omkostninger og resultat (EBITDA) før afskrivninger">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0.5rem',
              background: `${COLORS.status.success}15`,
              borderRadius: '0.25rem',
              marginBottom: '0.5rem',
            }}>
              <span style={{ fontWeight: '600' }}>OMSÆTNING</span>
              <span style={{ fontWeight: '700', color: COLORS.status.success }}>
                {(metrics.totalRevenue / 1000000).toFixed(1)} M
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0.5rem',
              background: `${COLORS.status.error}15`,
              borderRadius: '0.25rem',
              marginBottom: '0.5rem',
            }}>
              <span style={{ fontWeight: '600' }}>OMKOSTNINGER</span>
              <span style={{ fontWeight: '700', color: COLORS.status.error }}>
                -{(metrics.totalOpex / 1000000).toFixed(1)} M
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0.75rem',
              background: metrics.ebitda > 0 ? `${COLORS.status.success}25` : `${COLORS.status.error}25`,
              borderRadius: '0.25rem',
              border: `2px solid ${metrics.ebitda > 0 ? COLORS.status.success : COLORS.status.error}`,
              marginTop: '0.5rem',
            }}>
              <span style={{ fontWeight: '700' }}>RESULTAT (EBITDA)</span>
              <span style={{ fontWeight: '800', fontSize: '1.1rem', color: metrics.ebitda > 0 ? COLORS.status.success : COLORS.status.error }}>
                {(metrics.ebitda / 1000000).toFixed(1)} M
              </span>
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />

            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Tooltip title={`Tilbagebetalingstid beregnes som: Total investering (${(metrics.totalCapex / 1000000).toFixed(1)}M DKK) divideret med årligt driftsresultat EBITDA (${(metrics.ebitda / 1000000).toFixed(1)}M DKK). Jo lavere, desto bedre - under 10 år er attraktivt.`}>
                  <div style={{ textAlign: 'center', cursor: 'help' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: COLORS.primary.green }}>
                      {metrics.paybackYears.toFixed(1)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>år</div>
                    <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                      Tilbagebetaling <InfoCircleOutlined style={{ fontSize: '0.7rem' }} />
                    </div>
                  </div>
                </Tooltip>
              </Col>
              <Col span={12}>
                <Tooltip title={`Nettomargin viser hvor meget der er tilbage efter alle omkostninger og afskrivninger. Beregnes som: Nettoresultat (${(metrics.netProfit / 1000000).toFixed(1)}M DKK) / Omsætning (${(metrics.totalRevenue / 1000000).toFixed(1)}M DKK) × 100 = ${metrics.netMargin.toFixed(0)}%. Dette er overskuddet i forhold til omsætningen.`}>
                  <div style={{ textAlign: 'center', cursor: 'help' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: COLORS.primary.green }}>
                      {metrics.netMargin.toFixed(0)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>%</div>
                    <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                      Overskudsgrad <InfoCircleOutlined style={{ fontSize: '0.7rem' }} />
                    </div>
                  </div>
                </Tooltip>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Finansielle Nøgletal */}
        <Col span={8}>
          <Card 
            title={
              <span>
                📈 Finansielle Nøgletal
                <Tooltip title="Investeringsanalyse over 25 år med 5% diskonteringsrente. NPV viser nutidsværdi, IRR forrentning, LCOE gennemsnitspris">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            <Tooltip title={`NPV (Nutidsværdi) beregner projektets værdi i dag ved at diskontere alle fremtidige pengestrømme tilbage til nutiden med 5% årlig rente over 25 år. Formel: NPV = -Investering (${(metrics.totalCapex / 1000000).toFixed(1)}M) + Sum af (EBITDA / (1,05)^år) for år 1-25. Positiv NPV betyder projektet er rentabelt.`}>
              <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>
                    NPV <InfoCircleOutlined style={{ fontSize: '0.7rem' }} />:
                  </span>
                  <span style={{ fontWeight: '600', color: metrics.npv > 0 ? COLORS.status.success : COLORS.status.error }}>
                    {(metrics.npv / 1000000).toFixed(1)} M DKK
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                  5% rente, 25 år
                </div>
              </div>
            </Tooltip>

            <Tooltip title={`IRR (Intern Rente) er den årlige forrentning projektet giver. Det er den rente hvor NPV bliver nul. Her approksimeret baseret på tilbagebetalingstid (${metrics.paybackYears.toFixed(1)} år). IRR over diskonteringsrenten (5%) betyder projektet er rentabelt. Jo højere IRR, desto bedre forrentning.`}>
              <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>
                    IRR <InfoCircleOutlined style={{ fontSize: '0.7rem' }} />:
                  </span>
                  <span style={{ fontWeight: '600', color: metrics.irr > metrics.discountRate ? COLORS.status.success : COLORS.status.warning }}>
                    {metrics.irr.toFixed(1)}%
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                  Intern rente
                </div>
              </div>
            </Tooltip>

            <Tooltip title={`LCOE (Levelized Cost of Energy) er den gennemsnitlige pris pr. MWh varme over projektets levetid. Beregnes som: Nutidsværdi af alle omkostninger (investering + OPEX) divideret med nutidsværdi af al produceret energi (${(metrics.annualHeatDelivery / 1000).toFixed(1)} GWh/år). Alle værdier diskonteret til nutidsværdi med 5% rente over 25 år.`}>
              <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>
                    LCOE <InfoCircleOutlined style={{ fontSize: '0.7rem' }} />:
                  </span>
                  <span style={{ fontWeight: '600' }}>
                    {metrics.lcoe.toFixed(0)} DKK/MWh
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                  Gennemsnitspris over {metrics.projectLife} år
                </div>
              </div>
            </Tooltip>

            <Divider style={{ margin: '0.75rem 0' }} />

            <div style={{ marginBottom: '0.5rem' }}>
              <h4 style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary, marginBottom: '0.5rem' }}>
                Varmepriser
                <Tooltip title="Fire forskellige varmepriser der viser energiomkostninger på forskellige niveauer - fra rå produktionspris til fuld kundepris med alle omkostninger.">
                  <InfoCircleOutlined style={{ marginLeft: '0.25rem', fontSize: '0.7rem' }} />
                </Tooltip>
              </h4>

              <Tooltip title={`Værkets produktionspris er omkostningen for energi der går gennem varmeværket (an-værk til ab-værk). Beregnes som: (Elkøb operational + Sol operational) / (Sol energi + Elnet energi) = (${(metrics.plantCost / 1000000).toFixed(1)}M DKK) / (${(metrics.plantEnergy / 1000).toFixed(1)} GWh) = ${metrics.plantProductionPrice.toFixed(0)} DKK/MWh. Inkluderer IKKE fabriksoverskudsvarme der går direkte til LTES.`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', cursor: 'help' }}>
                  <span style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                    Værkets pris (Sol + Elnet) <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:
                  </span>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {metrics.plantProductionPrice.toFixed(0)} DKK/MWh
                  </span>
                </div>
              </Tooltip>
              <div style={{ fontSize: '0.7rem', color: COLORS.ui.text.secondary, marginBottom: '0.5rem' }}>
                Gennem an-værk/ab-værk: {(metrics.plantEnergy / 1000).toFixed(1)} GWh
              </div>

              <Tooltip title={`Total rå net pris inkluderer både energi gennem værket OG fabriksoverskudsvarme der går direkte til LTES uden at gå gennem an-værk. Beregnes som: (Elkøb + Sol + Overskudsvarme) / (Sol + Elnet + Fabriksoverskudsvarme) = (${(metrics.totalRawCost / 1000000).toFixed(1)}M DKK) / (${(metrics.totalRawEnergy / 1000).toFixed(1)} GWh) = ${metrics.totalRawNetPrice.toFixed(0)} DKK/MWh. Dette er den gennemsnitlige omkostning per MWh varme inkl. billig fabriksoverskudsvarme.`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', cursor: 'help' }}>
                  <span style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                    Total rå net (inkl. overskudsvarme) <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:
                  </span>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {metrics.totalRawNetPrice.toFixed(0)} DKK/MWh
                  </span>
                </div>
              </Tooltip>
              <div style={{ fontSize: '0.7rem', color: COLORS.ui.text.secondary, marginBottom: '0.5rem' }}>
                Fabriksoverskudsvarme direkte til LTES: {(metrics.totalRawEnergy / 1000).toFixed(1)} GWh
              </div>

              <Tooltip title={`OPEX varmepris inkluderer KUN varmeselskabets driftsomkostninger (både variable energikøb og faste omkostninger som licens, IT, revision, administration, drift & vedligehold). Beregnes som: Varmeselskabets total OPEX / Leveret varme = (${(metrics.heatCompanyOpex / 1000000).toFixed(1)}M DKK) / (${(metrics.annualHeatDelivery / 1000).toFixed(1)} GWh) = ${metrics.opexHeatPrice.toFixed(0)} DKK/MWh. Inkluderer IKKE afskrivninger eller elselskabets omkostninger.`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', cursor: 'help' }}>
                  <span style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                    OPEX varmepris (varmeselskab) <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:
                  </span>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {metrics.opexHeatPrice.toFixed(0)} DKK/MWh
                  </span>
                </div>
              </Tooltip>
              <div style={{ fontSize: '0.7rem', color: COLORS.ui.text.secondary, marginBottom: '0.5rem' }}>
                Kun varmeselskabets variable + faste omkostninger
              </div>

              <Tooltip title={`Varmepris til kunder er den FULDE pris der skal opkræves for at dække ALLE omkostninger inkl. afskrivninger. Som non-profit varmeselskab skal denne pris dække: alle OPEX omkostninger (${(metrics.heatCompanyOpex / 1000000).toFixed(1)}M DKK) + alle afskrivninger (${(metrics.heatCompanyDepreciation / 1000000).toFixed(1)}M DKK) = ${((metrics.heatCompanyOpex + metrics.heatCompanyDepreciation) / 1000000).toFixed(1)}M DKK total. Divideret med leveret varme (${(metrics.annualHeatDelivery / 1000).toFixed(1)} GWh) = ${metrics.fullHeatPrice.toFixed(0)} DKK/MWh.`}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  background: `${COLORS.primary.green}15`,
                  borderRadius: '0.25rem',
                  border: `1px solid ${COLORS.primary.green}30`,
                  cursor: 'help',
                }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: COLORS.ui.text.primary }}>
                    Varmepris til kunder <InfoCircleOutlined style={{ fontSize: '0.75rem' }} />:
                  </span>
                  <span style={{ fontWeight: '700', fontSize: '1rem', color: COLORS.primary.green }}>
                    {metrics.fullHeatPrice.toFixed(0)} DKK/MWh
                  </span>
                </div>
              </Tooltip>
              <div style={{ fontSize: '0.7rem', color: COLORS.ui.text.secondary, marginTop: '0.25rem' }}>
                Varmeselskabets OPEX + Afskrivninger / {((stats.netDelivery || 0)/1000).toFixed(1)} GWh netto
              </div>
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Payback:</span>
                <span style={{ fontWeight: '600' }}>
                  {metrics.paybackYears.toFixed(1)} år
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary }}>
                CAPEX {(metrics.totalCapex/1000000).toFixed(0)}M / EBITDA {(metrics.ebitda/1000000).toFixed(1)}M
              </div>
            </div>
          </Card>
        </Col>

        {/* Indtægter */}
        <Col span={8}>
          <Card 
            title={
              <span>
                💰 Indtægter
                <Tooltip title="Samlet årlig omsætning fra alle indtægtskilder: varmesalg, elsalg, systemydelser og kapacitetsbetaling">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {/* Varmesalg */}
            {metrics.revenues.varmesalg > 0 && (
              <Tooltip title={`Varmesalg beregnes dynamisk som non-profit: (Varmeselskabets OPEX ${(metrics.heatCompanyOpex / 1000000).toFixed(1)}M + Afskrivninger ${(metrics.heatCompanyDepreciation / 1000000).toFixed(1)}M) / Leveret varme ${((stats.netDelivery || 0) / 1000).toFixed(1)} GWh = ${metrics.fullHeatPrice.toFixed(0)} DKK/MWh. Total: ${((stats.netDelivery || 0) / 1000).toFixed(1)} GWh × ${metrics.fullHeatPrice.toFixed(0)} DKK/MWh = ${formatCurrency(metrics.revenues.varmesalg, 'DKK', 2)}.`}>
                <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Varmesalg <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(metrics.revenues.varmesalg, 'DKK', 2)}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary, textAlign: 'right' }}>
                    {((stats.netDelivery || 0) / 1000).toFixed(1)} GWh
                  </div>
                </div>
              </Tooltip>
            )}

            {/* Turbine elsalg */}
            {metrics.revenues.elsalgTurbine > 0 && (
              <Tooltip title={`Turbine elsalg: Når HTES aflades gennem turbine produceres el som sælges til spot-pris. Total produceret: ${((stats.turbineElectricityProduced || 0) / 1000).toFixed(2)} GWh. Gennemsnitlig spotpris: ${((metrics.revenues.elsalgTurbine / (stats.turbineElectricityProduced || 1)) || 0).toFixed(0)} DKK/MWh. Indtægt: ${((stats.turbineElectricityProduced || 0) / 1000).toFixed(2)} GWh × spotpris = ${formatCurrency(metrics.revenues.elsalgTurbine, 'DKK', 2)}.`}>
                <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Turbine elsalg <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(metrics.revenues.elsalgTurbine, 'DKK', 2)}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary, textAlign: 'right' }}>
                    {((stats.turbineElectricityProduced || 0) / 1000).toFixed(2)} GWh
                  </div>
                </div>
              </Tooltip>
            )}

            {/* Batteri elsalg */}
            {metrics.revenues.elsalgBatteri > 0 && (
              <Tooltip title={`Batteri elsalg (arbitrage): Batteriet køber billig el og sælger når prisen er høj. Total solgt: ${((stats.batteryElectricitySold || 0) / 1000).toFixed(2)} GWh. Gennemsnitlig salgspris: ${((metrics.revenues.elsalgBatteri / (stats.batteryElectricitySold || 1)) || 0).toFixed(0)} DKK/MWh. Indtægt: ${((stats.batteryElectricitySold || 0) / 1000).toFixed(2)} GWh × spotpris = ${formatCurrency(metrics.revenues.elsalgBatteri, 'DKK', 2)}.`}>
                <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Batteri elsalg <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(metrics.revenues.elsalgBatteri, 'DKK', 2)}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary, textAlign: 'right' }}>
                    {((stats.batteryElectricitySold || 0) / 1000).toFixed(2)} GWh
                  </div>
                </div>
              </Tooltip>
            )}

            {/* Systemydelser Elkedel */}
            {metrics.revenues.systemydelserElkedel > 0 && (
              <Tooltip title={`Systemydelser fra elkedel (mFRR + aFRR): Når elkedlen IKKE bruges til varmeproduktion, kan den levere balanceydelser. Total disponible timer: ${metrics.serviceHours}. Fordeling: 67% mFRR (${Math.round(metrics.serviceHours * 0.67)} timer × 30 MW × 100 DKK/MW/h = ${formatCurrency(Math.round(metrics.serviceHours * 0.67) * 30 * 100, 'DKK', 0)}) + 33% aFRR (${Math.round(metrics.serviceHours * 0.33)} timer × 30 MW × 150 DKK/MW/h = ${formatCurrency(Math.round(metrics.serviceHours * 0.33) * 30 * 150, 'DKK', 0)}). Total: ${formatCurrency(metrics.revenues.systemydelserElkedel, 'DKK', 2)}.`}>
                <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Systemydelser (Elkedel) <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(metrics.revenues.systemydelserElkedel, 'DKK', 2)}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary, textAlign: 'right' }}>
                    {metrics.serviceHours} timer
                  </div>
                </div>
              </Tooltip>
            )}

            {/* Systemydelser Batteri */}
            {metrics.revenues.systemydelserBatteri > 0 && (
              <Tooltip title={`Systemydelser fra batteri: Når batteriet er idle (ikke lader/aflader) og SOC ≥ 10%, kan det levere kapacitetsydelser. Timer: ${stats.batteryServiceHours || 0}. Formel: ${stats.batteryServiceHours || 0} timer × 3 MW × 125 DKK/MW/h = ${formatCurrency(metrics.revenues.systemydelserBatteri, 'DKK', 2)}. Dette er konservativ DK2 systemydelse-pris.`}>
                <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Systemydelser (Batteri) <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(metrics.revenues.systemydelserBatteri, 'DKK', 2)}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary, textAlign: 'right' }}>
                    {stats.batteryServiceHours || 0} timer
                  </div>
                </div>
              </Tooltip>
            )}

            {/* Systemydelser Turbine */}
            {metrics.revenues.systemydelserTurbine > 0 && (
              <Tooltip title={`Systemydelser fra turbine: Når turbine er tilgængelig (HTES kan aflade) men IKKE aktivt producerer, kan den levere kapacitetsydelser. Timer: ${stats.turbineServiceHours || 0}. Formel: ${stats.turbineServiceHours || 0} timer × 10 MW × 100 DKK/MW/h = ${formatCurrency(metrics.revenues.systemydelserTurbine, 'DKK', 2)}. Kræver at HTES har tilstrækkelig temperatur og energi.`}>
                <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Systemydelser (Turbine) <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(metrics.revenues.systemydelserTurbine, 'DKK', 2)}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary, textAlign: 'right' }}>
                    {stats.turbineServiceHours || 0} timer
                  </div>
                </div>
              </Tooltip>
            )}

            {/* Kapacitetsbetaling */}
            {metrics.revenues.kapacitetsbetaling > 0 && (
              <Tooltip title={`Kapacitetsbetaling: Daglig betaling for at stille kapacitet til rådighed for elnettet. Beregnes som: 366 dage × 2000 DKK/dag = ${formatCurrency(366 * 2000, 'DKK', 0)} total. Fordeles 60% til varmeselskab (${formatCurrency(366 * 2000 * 0.6, 'DKK', 0)}) og 40% til elselskab (${formatCurrency(366 * 2000 * 0.4, 'DKK', 0)}) baseret på anlægskapacitet. Her vises den kombinerede værdi.`}>
                <div style={{ marginBottom: '0.5rem', cursor: 'help' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>Kapacitetsbetaling <InfoCircleOutlined style={{ fontSize: '0.65rem' }} />:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(metrics.revenues.kapacitetsbetaling, 'DKK', 2)}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: COLORS.ui.text.secondary, textAlign: 'right' }}>
                    366 dage
                  </div>
                </div>
              </Tooltip>
            )}
            
            <Divider style={{ margin: '0.75rem 0' }} />
            
            <Statistic
              title="TOTAL"
              value={metrics.totalRevenue}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.status.success, fontSize: '1.25rem', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 2: Afskrivninger, OPEX, CAPEX */}
      <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
        {/* Afskrivninger */}
        <Col span={8}>
          <Card 
            title={
              <span>
                📉 Afskrivninger
                <Tooltip title="Årlige afskrivninger baseret på anlæggenes levetid">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(metrics.depreciation).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}:
                    </span>
                    <span style={{ fontWeight: '600' }}>
                      {(value).toFixed(2)} M DKK/år
                    </span>
                  </div>
                </div>
              )
            ))}
            
            <Divider style={{ margin: '0.75rem 0' }} />
            
            <Statistic
              title="Total årlig afskrivning"
              value={metrics.totalDepreciation}
              precision={0}
              suffix="DKK/år"
              valueStyle={{ color: COLORS.status.error, fontSize: '1.25rem', fontWeight: 'bold' }}
            />
          </Card>
        </Col>

        {/* OPEX */}
        <Col span={8}>
          <Card 
            title={
              <span>
                ⚡ OPEX - Driftsomkostninger
                <Tooltip title="Samlede driftsomkostninger opdelt i variable energikøb og faste driftsomkostninger">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            <div style={{ marginBottom: '0.75rem' }}>
              <h4 style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary, marginBottom: '0.5rem' }}>
                Variable omkostninger
              </h4>
              {Object.entries({
                elkøb: 'Elkøb (Spot + TSO)',
                sol: 'Sol (Spotpris)',
                overskudsvarme: 'Overskudsvarme (150 DKK/MWh)',
              }).map(([key, label]) => (
                metrics.opex.variable[key] > 0 && (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>{label}:</span>
                    <span style={{ fontWeight: '600' }}>
                      {(metrics.opex.variable[key] / 1000000).toFixed(2)} M
                    </span>
                  </div>
                )
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: `1px solid ${COLORS.ui.border}` }}>
                <span style={{ fontWeight: '600' }}>Total variable:</span>
                <span style={{ fontWeight: '700' }}>
                  {(metrics.totalVariableOpex / 1000000).toFixed(2)} M
                </span>
              </div>
            </div>

            <div>
              <h4 style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary, marginBottom: '0.5rem' }}>
                Faste omkostninger
              </h4>
              {Object.entries({
                forsyningDkLicens: 'Forsyning DK licens',
                it: 'IT',
                revision: 'Revision',
                administration: 'Administration',
                driftVedligehold: 'D&V',
              }).map(([key, label]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>{label}:</span>
                  <span style={{ fontWeight: '600' }}>
                    {(metrics.opex.fixed[key] / 1000000).toFixed(2)} M
                  </span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: `1px solid ${COLORS.ui.border}` }}>
                <span style={{ fontWeight: '600' }}>Total faste:</span>
                <span style={{ fontWeight: '700' }}>
                  {(metrics.totalFixedOpex / 1000000).toFixed(2)} M
                </span>
              </div>
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />

            <Statistic
              title="Total OPEX"
              value={metrics.totalOpex}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.status.error, fontSize: '1.25rem', fontWeight: 'bold' }}
            />
          </Card>
        </Col>

        {/* CAPEX */}
        <Col span={8}>
          <Card 
            title={
              <span>
                🏗️ CAPEX - Investeringer
                <Tooltip title="Samlede investeringsomkostninger for etablering af fjernvarmesystemet">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(metrics.capex).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary }}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}:
                    </span>
                    <span style={{ fontWeight: '600' }}>
                      {(value).toFixed(1)} M DKK
                    </span>
                  </div>
                </div>
              )
            ))}
            
            <Divider style={{ margin: '0.75rem 0' }} />
            
            <Statistic
              title="Total CAPEX"
              value={metrics.totalCapex}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.status.error, fontSize: '1.25rem', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 3: P&L */}
      <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
        {/* Resultatopgørelse (P&L) - Full width */}
        <Col span={24}>
          <Card 
            title={
              <span>
                � Resultatopgørelse (P&L)
                <Tooltip title="Komplet resultatopgørelse fra indtægt til nettoresultat">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            style={{ 
              borderColor: metrics.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
            }}
            size="small"
          >
            <Row gutter={16}>
              <Col span={4}>
                <Tooltip title={`Total årlig indtægt fra alle kilder: Varmesalg (${(metrics.revenues.varmesalg / 1000000).toFixed(1)}M), Elsalg turbine+batteri (${((metrics.revenues.elsalgTurbine + metrics.revenues.elsalgBatteri) / 1000000).toFixed(1)}M), Systemydelser (${((metrics.revenues.systemydelserElkedel + metrics.revenues.systemydelserBatteri + metrics.revenues.systemydelserTurbine) / 1000000).toFixed(1)}M), og Kapacitetsbetaling (${(metrics.revenues.kapacitetsbetaling / 1000000).toFixed(1)}M).`}>
                  <div style={{ cursor: 'help' }}>
                    <Statistic
                      title={<span>Indtægt <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                      value={metrics.totalRevenue}
                      precision={0}
                      suffix="DKK"
                      prefix="+"
                      valueStyle={{ color: COLORS.status.success, fontWeight: 'bold' }}
                    />
                  </div>
                </Tooltip>
              </Col>
              <Col span={4}>
                <Tooltip title={`Driftsomkostninger (OPEX) inkluderer variable energikøb (${(metrics.totalVariableOpex / 1000000).toFixed(1)}M): Elkøb (${(metrics.opex.variable.elkøb / 1000000).toFixed(1)}M), Sol (${(metrics.opex.variable.sol / 1000000).toFixed(1)}M), Overskudsvarme (${(metrics.opex.variable.overskudsvarme / 1000000).toFixed(1)}M). Plus faste omkostninger (${(metrics.totalFixedOpex / 1000000).toFixed(1)}M): Licens, IT, Revision, Administration, D&V.`}>
                  <div style={{ cursor: 'help' }}>
                    <Statistic
                      title={<span>OPEX <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                      value={metrics.totalOpex}
                      precision={0}
                      suffix="DKK"
                      prefix="−"
                      valueStyle={{ color: COLORS.status.error, fontWeight: 'bold' }}
                    />
                  </div>
                </Tooltip>
              </Col>
              <Col span={4}>
                <Tooltip title={`EBITDA (Earnings Before Interest, Taxes, Depreciation, Amortization) er driftsresultatet FØR afskrivninger. Beregnes som: Indtægt (${(metrics.totalRevenue / 1000000).toFixed(1)}M) minus OPEX (${(metrics.totalOpex / 1000000).toFixed(1)}M) = ${(metrics.ebitda / 1000000).toFixed(1)}M DKK. Dette viser den rene driftsøkonomi uden hensyntagen til investeringer.`}>
                  <div style={{ cursor: 'help' }}>
                    <Statistic
                      title={<span>EBITDA <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                      value={metrics.ebitda}
                      precision={0}
                      suffix="DKK"
                      prefix={metrics.ebitda >= 0 ? "+" : "−"}
                      valueStyle={{
                        color: metrics.ebitda >= 0 ? COLORS.status.success : COLORS.status.error,
                        fontWeight: 'bold',
                      }}
                    />
                  </div>
                </Tooltip>
              </Col>
              <Col span={4}>
                <Tooltip title={`Årlige afskrivninger fordelt på anlæg: HTES ${(metrics.depreciation.htes || 0).toFixed(1)}M (25 år), Ledningsnet ${(metrics.depreciation.ledningsnet || 0).toFixed(1)}M (50 år), Elkedel ${(metrics.depreciation.elkedel || 0).toFixed(1)}M (25 år), Batterier ${(metrics.depreciation.batterier || 0).toFixed(1)}M (10 år), Turbine ${(metrics.depreciation.turbine || 0).toFixed(1)}M (15 år), osv. Total: ${(metrics.totalDepreciation / 1000000).toFixed(1)}M DKK årligt.`}>
                  <div style={{ cursor: 'help' }}>
                    <Statistic
                      title={<span>Afskrivning <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                      value={metrics.totalDepreciation}
                      precision={0}
                      suffix="DKK"
                      prefix="−"
                      valueStyle={{ color: COLORS.status.error, fontWeight: 'bold' }}
                    />
                  </div>
                </Tooltip>
              </Col>
              <Col span={4}>
                <Tooltip title={`Nettoresultat er det endelige overskud efter ALLE omkostninger inkl. afskrivninger. Beregnes som: EBITDA (${(metrics.ebitda / 1000000).toFixed(1)}M) minus Afskrivninger (${(metrics.totalDepreciation / 1000000).toFixed(1)}M) = ${(metrics.netProfit / 1000000).toFixed(1)}M DKK. Dette er det årlige overskud projektet genererer. Nettomargin: ${metrics.netMargin.toFixed(0)}%.`}>
                  <div style={{ cursor: 'help' }}>
                    <Statistic
                      title={<span>Nettoresultat <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                      value={Math.abs(metrics.netProfit)}
                      precision={0}
                      suffix="DKK"
                      prefix={metrics.netProfit >= 0 ? "+" : "−"}
                      valueStyle={{
                        color: metrics.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
                        fontWeight: 'bold',
                        fontSize: '1.125rem',
                      }}
                    />
                  </div>
                </Tooltip>
              </Col>
              <Col span={4}>
                <Statistic
                  title="Nettomargin"
                  value={metrics.netMargin}
                  precision={1}
                  suffix="%"
                  valueStyle={{ 
                    color: metrics.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
                  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CombinedCompanyFinancials;
